// AdminPage.js
// Holds rows for each section that an admin person might need to interact with.
import { useState } from "react";
import Select from "react-select";
import { shallow } from "zustand/shallow";
import { startTask, getTaskStatus } from "../../api/TasksApi";
import {
  addEmployeePosition,
  updateEmployeePosition,
  searchEmployee,
  changePassword,
  changeUserStatus,
  getAdminUsers,
  makeAdmin,
  removeAdmin,
  addSafetyTeamMember,
  removeSafetyTeamMember,
  getSafetyTeamMembers,
  addWasteDisposalTeamMember,
  removeWasteDisposalTeamMember,
  getWasteDisposalTeamMembers,
  getProjectUnitTableNames,
  downloadProjectUnitTemplate,
  downloadBlankProjectUnitTemplate,
  uploadProjectUnits,
} from "../../api/adminApi";
import { useUserState } from "../../context/user";
import { useAppStore } from "../../store/appStore";
import { ChangePasswordModal } from "./components/changePasswordModal";
import { ChangeUserStatusModal } from "./components/changeUserStatusModal";
import styles from "./AdminPage.module.css";
import { AdminPageSelectStyles } from "../common/delSelect";

export const AdminPage = () => {
  // Global data
  const user = useUserState().currentUser;
  const employees = useAppStore((s) => s.employees.list);
  const terminatedEmployees = useAppStore((s) => s.employees.terminatedList);
  const allSearchEmployees = [...employees, ...terminatedEmployees];
  const allEmployeePositions = useAppStore((s) => s.employeePositions.list);
  // Zustand actions
  const { fetchEmployeePositions } = useAppStore(
    (s) => s.employeePositionsActions,
  );
  const fetchEmployees = useAppStore((s) => s.employeesActions.fetchEmployees);
  const fetchTerminatedEmployees = useAppStore(
    (s) => s.employeesActions.fetchTerminatedEmployees,
  );
  const {
    setShowDatabaseUpdatesSection,
    setShowEmployeeUpdatesSection,
    setShowAdminUsersSection,
    setShowProjectUnitsSection,
  } = useAppStore((s) => s.adminPageActions);
  const {
    showDatabaseUpdatesSection,
    showEmployeeUpdatesSection,
    showAdminUsersSection,
    showProjectUnitsSection,
  } = useAppStore((s) => s.adminPage, shallow);

  // UI State
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification,
  );

  // Task actions and state
  const { setCurrentTaskId, setTaskStatus, setTaskError, clearTask } =
    useAppStore((s) => s.adminPageActions);
  const { currentTaskId, taskStatus, taskError } = useAppStore(
    (s) => s.adminPage,
    shallow,
  );

  // Admin Task Actions - Projects, Units, Employee Updates
  // Based on sending celery task names to server to be processed and sent back info letting the user know if it was successful
  const pollTaskStatus = async (taskId, displayTaskName) => {
    const maxAttempts = 300; // 10 minutes max (2 second intervals)
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await getTaskStatus(taskId);
        const { state, ready, ok, error: taskError } = response.data;
        setTaskStatus(state.toLowerCase());
        if (ready && ok) {
          // Task completed successfully
          hideLoading();
          showNotification({
            title: "Success",
            message: `${displayTaskName} completed successfully`,
            type: "success",
          });
          clearTask();
        } else if (state === "FAILURE" || state === "REVOKED") {
          // Task failed
          hideLoading();
          setTaskError(taskError || "Task failed");
          showNotification({
            title: "Error",
            message: taskError || "Task failed",
            type: "error",
          });
          clearTask();
        } else if (attempts < maxAttempts) {
          // Still processing, poll again
          attempts++;
          setTimeout(poll, 2000);
        } else {
          // Timeout
          hideLoading();
          showNotification({
            title: "Error",
            message: `${displayTaskName} timed out`,
            type: "error",
          });
          clearTask();
        }
      } catch (error) {
        console.error(`Error polling ${displayTaskName} status:`, error);
        hideLoading();
        showNotification({
          title: "Error",
          message: `Error checking ${displayTaskName} status`,
          type: "error",
        });
        clearTask();
      }
    };
    poll();
  };

  const handleDBUpdateTask = async (celeryTaskName, displayTaskName) => {
    showLoading(`Processing ${displayTaskName}...`);
    try {
      const response = await startTask(celeryTaskName);
      const taskId = response.data.task_id;
      setCurrentTaskId(taskId);
      setTaskStatus("pending");
      pollTaskStatus(taskId, displayTaskName);
    } catch (error) {
      console.error("Error starting task:", error);
      showNotification({
        title: "Error",
        message: "Error starting task",
        type: "error",
      });
      clearTask();
      hideLoading();
    }
  };

  // Employee Position Actions and State
  const [newEmployeePositionDescription, setNewEmployeePositionDescription] =
    useState("");
  const handleAddEmployeePosition = async (newEmployeePositionDescription) => {
    // Check if the new employee position is in the current employee position list
    const newEmployeePosition = allEmployeePositions.find(
      (position) => position.label === newEmployeePositionDescription,
    );
    if (newEmployeePosition) {
      if (newEmployeePosition.status === 1) {
        showNotification({
          message: "Employee position already exists",
          type: "error",
        });
      } else {
        showNotification({
          message:
            "Employee position is inactive and can be reactivated in the 'Update Employee Positions' section",
          type: "error",
        });
      }
      return;
    }
    // Add the new employee position to the current employee position list
    showLoading("Adding new employee position...");
    try {
      const response = await addEmployeePosition(
        newEmployeePositionDescription,
      );
      if (response.success) {
        showNotification({
          message: "Employee position added successfully",
          type: "success",
        });
        fetchEmployeePositions();
      }
    } catch (error) {
      console.error("Error adding employee position:", error);
      showNotification({
        message: "Error adding employee position",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const [selectedEmployeePosition, setSelectedEmployeePosition] =
    useState(null);
  const handleUpdateEmployeePosition = async (selectedEmployeePosition) => {
    if (!selectedEmployeePosition) {
      showNotification({
        message: "Please select an employee position to update",
        type: "error",
      });
      return;
    }
    // Update the selected employee position in the current employee position list
    showLoading("Updating employee position...");
    try {
      let status;
      if (selectedEmployeePosition.status === 1) {
        status = 0;
      } else {
        status = 1;
      }
      const response = await updateEmployeePosition(
        selectedEmployeePosition.value,
        status,
      );
      if (response.success) {
        showNotification({
          message: "Employee position updated successfully",
          type: "success",
        });
        setSelectedEmployeePosition(null);
        await fetchEmployeePositions();
      }
    } catch (error) {
      console.error("Error updating employee position:", error);
      showNotification({
        message: "Error updating employee position",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  // Employee Updates Actions and State
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeInfo, setShowEmployeeInfo] = useState(false);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [showUpdateButtons, setShowUpdateButtons] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showUserStatusModal, setShowUserStatusModal] = useState(false);

  // Searching for an employee and selecting an employee will trigger an API call to the database to retrieve information and display it. It will also make the info section appear as well. Once an employee is selected, also the action buttons will be enabled.
  const handleSearchEmployee = async (selectedEmployee) => {
    if (!selectedEmployee) {
      setSelectedEmployee(null);
      setShowEmployeeInfo(false);
      setEmployeeInfo(null);
      setShowUpdateButtons(false);
      return;
    }
    try {
      showLoading("Searching for employee...");
      setSelectedEmployee(selectedEmployee);
      const response = await searchEmployee(selectedEmployee.value);
      if (response.success) {
        setEmployeeInfo(response.data);
        setShowEmployeeInfo(true);
        setShowUpdateButtons(true);
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error searching for employee:", error);
      showNotification({
        title: "Error",
        message: "Error searching for employee",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  // Change Password Modal Actions and State
  const handleShowChangePasswordModal = () => {
    if (!selectedEmployee) {
      showNotification({
        title: "Error",
        message: "Please select an employee to change the password",
        type: "error",
      });
      return;
    }
    setShowChangePasswordModal(true);
  };

  const handleChangePassword = async (newPassword) => {
    try {
      showLoading("Changing password...");
      const response = await changePassword(
        selectedEmployee.value,
        newPassword,
      );
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Password changed successfully",
          type: "success",
        });
        setShowChangePasswordModal(false);
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showNotification({
        title: "Error",
        message: "Error changing password",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  // Change User Status Modal Actions
  const handleShowUserStatusModal = () => {
    if (!selectedEmployee) {
      showNotification({
        title: "Error",
        message: "Please select an employee to change the user status",
        type: "error",
      });
      return;
    }
    setShowUserStatusModal(true);
  };

  const handleChangeUserStatus = async (userStatus, days) => {
    try {
      showLoading("Changing user status...");
      const response = await changeUserStatus(
        selectedEmployee.value,
        userStatus,
        days,
      );
      if (response.success) {
        showNotification({
          title: "Success",
          message: "User status changed successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error changing user status:", error);
      showNotification({
        title: "Error",
        message: "Error changing user status",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleMakeAdmin = async () => {
    try {
      showLoading("Making admin...");
      const response = await makeAdmin(selectedEmployee.value);
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Admin made successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error making admin:", error);
      showNotification({
        title: "Error",
        message: "Error making admin",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleRemoveAdmin = async () => {
    try {
      showLoading("Removing admin...");
      const response = await removeAdmin(selectedEmployee.value);
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Admin removed successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error removing admin:", error);
      showNotification({
        title: "Error",
        message: "Error removing admin",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  // Admin Updates Actions and State
  const [adminUserList, setAdminUserList] = useState(null);

  const handleGetAdminUsers = async () => {
    try {
      showLoading("Getting admin users...");
      const response = await getAdminUsers();
      if (response.success) {
        setAdminUserList(response.data);
      }
    } catch (error) {
      console.error("Error getting admin users:", error);
      showNotification({
        title: "Error",
        message: "Error getting admin users",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleAddSafetyTeamMember = async () => {
    try {
      showLoading("Adding safety team member...");
      const response = await addSafetyTeamMember(selectedEmployee.value);
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Safety team member added successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error adding safety team member:", error);
      showNotification({
        title: "Error",
        message: "Error adding safety team member",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleRemoveSafetyTeamMember = async () => {
    try {
      showLoading("Removing safety team member...");
      const response = await removeSafetyTeamMember(selectedEmployee.value);
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Safety team member removed successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error removing safety team member:", error);
      showNotification({
        title: "Error",
        message: "Error removing safety team member",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const [safetyTeamMemberList, setSafetyTeamMemberList] = useState(null);

  const handleGetSafetyTeamMembers = async () => {
    try {
      showLoading("Getting safety team members...");
      const response = await getSafetyTeamMembers();
      if (response.success) {
        setSafetyTeamMemberList(response.data);
      }
    } catch (error) {
      console.error("Error getting safety team members:", error);
      showNotification({
        title: "Error",
        message: "Error getting safety team members",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  // Waste Disposal Team Actions and State
  const handleAddWasteDisposalTeamMember = async () => {
    try {
      showLoading("Adding waste disposal team member...");
      const response = await addWasteDisposalTeamMember(selectedEmployee.value);
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Waste disposal team member added successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error adding waste disposal team member:", error);
      showNotification({
        title: "Error",
        message: "Error adding waste disposal team member",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleRemoveWasteDisposalTeamMember = async () => {
    try {
      showLoading("Removing waste disposal team member...");
      const response = await removeWasteDisposalTeamMember(
        selectedEmployee.value,
      );
      if (response.success) {
        showNotification({
          title: "Success",
          message: "Waste disposal team member removed successfully",
          type: "success",
        });
        await fetchEmployees();
        await fetchTerminatedEmployees();
        const response = await searchEmployee(selectedEmployee.value);
        if (response.success) {
          setEmployeeInfo(response.data);
          setShowEmployeeInfo(true);
          setShowUpdateButtons(true);
        }
      } else {
        showNotification({
          title: "Error",
          message: response.error,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error removing waste disposal team member:", error);
      showNotification({
        title: "Error",
        message: "Error removing waste disposal team member",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const [wasteDisposalTeamMemberList, setWasteDisposalTeamMemberList] =
    useState(null);

  const handleGetWasteDisposalTeamMembers = async () => {
    try {
      showLoading("Getting waste disposal team members...");
      const response = await getWasteDisposalTeamMembers();
      if (response.success) {
        setWasteDisposalTeamMemberList(response.data);
      }
    } catch (error) {
      console.error("Error getting waste disposal team members:", error);
      showNotification({
        title: "Error",
        message: "Error getting waste disposal team members",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  console.log(selectedEmployee);

  // Project Specific Units Actions and State
  const [projectUnitMode, setProjectUnitMode] = useState("new"); // "new" or "existing"
  const [existingTableNames, setExistingTableNames] = useState([]);
  const [selectedTableName, setSelectedTableName] = useState(null);
  const [projectUnitFile, setProjectUnitFile] = useState(null);
  const [projectUnitMessage, setProjectUnitMessage] = useState(null);

  const handleFetchTableNames = async () => {
    try {
      showLoading("Fetching table names...");
      const response = await getProjectUnitTableNames();
      if (response.success) {
        setExistingTableNames(
          response.tables.map((name) => ({ value: name, label: name })),
        );
      }
    } catch (error) {
      console.error("Error fetching table names:", error);
      showNotification({
        message: "Error fetching table names",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleExpandProjectUnitsSection = (currentState) => {
    setShowProjectUnitsSection(!currentState);
    if (!currentState) {
      handleFetchTableNames();
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      showLoading("Downloading template...");
      let response;
      if (projectUnitMode === "new") {
        response = await downloadBlankProjectUnitTemplate();
      } else {
        if (!selectedTableName) {
          showNotification({
            message: "Please select a table to download",
            type: "error",
          });
          return;
        }
        response = await downloadProjectUnitTemplate(selectedTableName.value);
      }
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        projectUnitMode === "new"
          ? "project_specific_units_template.csv"
          : `${selectedTableName.value}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      showNotification({
        message: "Error downloading template",
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleUploadProjectUnits = async () => {
    if (!projectUnitFile) {
      showNotification({
        message: "Please select a CSV file to upload",
        type: "error",
      });
      return;
    }
    if (projectUnitMode === "existing" && !selectedTableName) {
      showNotification({
        message: "Please select an existing table",
        type: "error",
      });
      return;
    }
    try {
      showLoading("Uploading project units...");
      const response = await uploadProjectUnits(projectUnitFile);
      if (response.success) {
        setProjectUnitMessage({
          type: "success",
          text: response.message || "Upload successful",
        });
        showNotification({
          message: "Project units uploaded successfully",
          type: "success",
        });
        setProjectUnitFile(null);
        setSelectedTableName(null);
        handleFetchTableNames();
      } else {
        setProjectUnitMessage({
          type: "error",
          text: response.error || "Upload failed",
        });
      }
    } catch (error) {
      console.error("Error uploading project units:", error);
      const errorMsg =
        error.response?.data?.error || "Error uploading project units";
      setProjectUnitMessage({ type: "error", text: errorMsg });
      showNotification({
        message: errorMsg,
        type: "error",
      });
    } finally {
      hideLoading();
    }
  };

  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  return (
    <div className={styles.adminPageContainer}>
      <div className={styles.adminPageHeader}>
        <h2>Admin</h2>
      </div>
      <div className={styles.adminPageContent}>
        <div className={styles.adminPageSection}>
          <div
            className={styles.adminPageSectionHeader}
            onClick={() => {
              setShowDatabaseUpdatesSection(!showDatabaseUpdatesSection);
            }}
          >
            <h3>Database Updates {showDatabaseUpdatesSection ? "▼" : "▲"}</h3>
          </div>
          {showDatabaseUpdatesSection && (
            <div className={styles.adminPageSectionRow}>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h4>
                    Click on a button to update the database with information
                    from the Deltek API
                  </h4>
                </div>
                <div className={styles.adminPageSectionRowItemButtons}>
                  <button
                    onClick={() =>
                      handleDBUpdateTask(
                        "db_sync.pull_deltek_projects",
                        "Projects",
                      )
                    }
                  >
                    Update Projects
                  </button>
                  <button
                    onClick={() =>
                      handleDBUpdateTask(
                        "db_sync.pull_deltek_unit_hub_info",
                        "Units",
                      )
                    }
                  >
                    Update Units
                  </button>
                  <button
                    onClick={() =>
                      handleDBUpdateTask(
                        "db_sync.pull_deltek_emps",
                        "Employees",
                      )
                    }
                  >
                    Update Employees
                  </button>
                </div>
              </div>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h4>Add an Employee Position to the database</h4>
                </div>
                <div className={styles.adminPageSectionRowItemInput}>
                  <input
                    type="text"
                    placeholder="Type New Employee Position"
                    value={newEmployeePositionDescription}
                    onChange={(e) =>
                      setNewEmployeePositionDescription(e.target.value)
                    }
                  />
                  <button
                    onClick={() =>
                      handleAddEmployeePosition(newEmployeePositionDescription)
                    }
                    disabled={!newEmployeePositionDescription}
                  >
                    Add Position
                  </button>
                </div>
              </div>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h4>Update an Employee Position in the database</h4>
                </div>
                <div className={styles.adminPageSectionRowItemInput}>
                  <Select
                    className={styles.adminPageSectionRowItemInputSelect}
                    value={selectedEmployeePosition}
                    onChange={(selectedOption) =>
                      setSelectedEmployeePosition(selectedOption)
                    }
                    options={allEmployeePositions.map((position) => ({
                      value: position.cat_id,
                      label: position.label,
                      status: position.status,
                    }))}
                    isClearable={true}
                    isSearchable={true}
                    placeholder="Select Employee Position"
                    styles={AdminPageSelectStyles}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition="fixed"
                    menuPlacement="auto"
                  />
                  <button
                    onClick={() =>
                      handleUpdateEmployeePosition(selectedEmployeePosition)
                    }
                    disabled={!selectedEmployeePosition}
                  >
                    {selectedEmployeePosition?.status === 1
                      ? "Deactivate Position"
                      : "Activate Position"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.adminPageSection}>
          <div
            className={styles.adminPageSectionHeader}
            onClick={() => {
              setShowEmployeeUpdatesSection(!showEmployeeUpdatesSection);
            }}
          >
            <h3>Employee Updates {showEmployeeUpdatesSection ? "▼" : "▲"}</h3>
          </div>
          {showEmployeeUpdatesSection && (
            <div>
              <div className={styles.adminPageSectionRow}>
                <div className={styles.adminPageSectionRowItem}>
                  <div className={styles.adminPageSectionRowHeader}>
                    <h4>Search for an employee to update</h4>
                  </div>
                  <div className={styles.adminPageSectionRowItemInput}>
                    <Select
                      className={styles.adminPageSectionRowItemInputSelect}
                      value={selectedEmployee}
                      onChange={(selectedOption) =>
                        handleSearchEmployee(selectedOption)
                      }
                      options={allSearchEmployees.map((employee) => ({
                        value: employee.employee_id,
                        label: employee.label,
                      }))}
                      isClearable={true}
                      isSearchable={true}
                      placeholder="Select Employee"
                      styles={AdminPageSelectStyles}
                      menuPortalTarget={menuPortalTarget}
                      menuPosition="fixed"
                      menuPlacement="auto"
                    />
                  </div>
                </div>
              </div>
              {showEmployeeInfo && (
                <div className={styles.adminPageSectionRow}>
                  <div className={styles.adminPageSectionRowItem}>
                    <div className={styles.adminPageSectionRowHeader}>
                      <h4>Employee Information</h4>
                    </div>
                    <div className={styles.adminPageSectionRowItemContent}>
                      <p>Employee ID: {employeeInfo.employee_id}</p>
                      <p>Username: {employeeInfo.username}</p>
                      <p>Employee Name: {employeeInfo.employee_name}</p>
                      <p>Employee Email: {employeeInfo.email}</p>
                      <p>Status: {employeeInfo.status}</p>
                      <p>Org: {employeeInfo.org}</p>
                      <p>Admin: {employeeInfo.admin ? "Yes" : "No"}</p>
                      <p>
                        Safety Team Member:{" "}
                        {employeeInfo.safety_team_member ? "Yes" : "No"}
                      </p>
                      <p>
                        Waste Disposal Team:{" "}
                        {employeeInfo.waste_disposal_team ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {showUpdateButtons && (
                <div className={styles.adminPageSectionRow}>
                  <div className={styles.adminPageSectionRowItem}>
                    <div className={styles.adminPageSectionRowHeader}>
                      <h3>
                        Click on a button to update the employee information
                      </h3>
                    </div>
                    <div className={styles.adminPageSectionRowItemButtons}>
                      <button onClick={handleShowChangePasswordModal}>
                        Change Password
                      </button>
                      <button onClick={handleShowUserStatusModal}>
                        Change User Status
                      </button>
                      <button
                        onClick={handleMakeAdmin}
                        disabled={!selectedEmployee || employeeInfo.admin}
                      >
                        Make Admin
                      </button>
                      <button
                        onClick={handleRemoveAdmin}
                        disabled={!selectedEmployee || !employeeInfo.admin}
                      >
                        Remove Admin
                      </button>
                      <button
                        onClick={handleAddSafetyTeamMember}
                        disabled={
                          !selectedEmployee || employeeInfo.safety_team_member
                        }
                      >
                        Add Safety Team Member
                      </button>
                      <button
                        onClick={handleRemoveSafetyTeamMember}
                        disabled={
                          !selectedEmployee || !employeeInfo.safety_team_member
                        }
                      >
                        Remove Safety Team Member
                      </button>
                      <button
                        onClick={handleAddWasteDisposalTeamMember}
                        disabled={
                          !selectedEmployee || employeeInfo.waste_disposal_team
                        }
                      >
                        Add Waste Disposal Team
                      </button>
                      <button
                        onClick={handleRemoveWasteDisposalTeamMember}
                        disabled={
                          !selectedEmployee || !employeeInfo.waste_disposal_team
                        }
                      >
                        Remove Waste Disposal Team
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.adminPageSection}>
          <div
            className={styles.adminPageSectionHeader}
            onClick={() => {
              setShowAdminUsersSection(!showAdminUsersSection);
            }}
          >
            <h3>Admin User Information {showAdminUsersSection ? "▼" : "▲"}</h3>
          </div>
          {showAdminUsersSection && (
            <div className={styles.adminPageSectionRow}>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h3>Get list of current admin users</h3>
                </div>
                <div className={styles.adminPageSectionRowItemButtons}>
                  <button onClick={() => handleGetAdminUsers()}>
                    Get Admin Users
                  </button>
                  <button onClick={() => handleGetSafetyTeamMembers()}>
                    Get Safety Team Members
                  </button>
                  <button onClick={() => handleGetWasteDisposalTeamMembers()}>
                    Get Waste Disposal Team
                  </button>
                </div>
              </div>
            </div>
          )}
          {showAdminUsersSection && adminUserList && (
            <div className={styles.adminPageSectionRow}>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h3>List of current admin users</h3>
                </div>
                <div className={styles.adminPageSectionRowItemContent}>
                  <ul>
                    {adminUserList.map((adminUser) => (
                      <li key={adminUser.employee_id}>
                        {adminUser.employee_id} - {adminUser.employee_name},
                        Status: {adminUser.status}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {showAdminUsersSection && safetyTeamMemberList && (
            <div className={styles.adminPageSectionRow}>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h3>List of current safety team members</h3>
                </div>
                <div className={styles.adminPageSectionRowItemContent}>
                  <ul>
                    {safetyTeamMemberList.map((safetyTeamMember) => (
                      <li key={safetyTeamMember.employee_id}>
                        {safetyTeamMember.employee_id} -{" "}
                        {safetyTeamMember.employee_name}, Status:{" "}
                        {safetyTeamMember.status}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {showAdminUsersSection && wasteDisposalTeamMemberList && (
            <div className={styles.adminPageSectionRow}>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h3>List of current waste disposal team members</h3>
                </div>
                <div className={styles.adminPageSectionRowItemContent}>
                  <ul>
                    {wasteDisposalTeamMemberList.map((member) => (
                      <li key={member.employee_id}>
                        {member.employee_id} - {member.employee_name}, Status:{" "}
                        {member.status}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.adminPageSection}>
          <div
            className={styles.adminPageSectionHeader}
            onClick={() =>
              handleExpandProjectUnitsSection(showProjectUnitsSection)
            }
          >
            <h3>
              Project Specific Units {showProjectUnitsSection ? "▼" : "▲"}
            </h3>
          </div>
          {showProjectUnitsSection && (
            <div className={styles.adminPageSectionRow}>
              <div className={styles.adminPageSectionRowItem}>
                <div className={styles.adminPageSectionRowHeader}>
                  <h4>Upload or update project-specific unit tables via CSV</h4>
                </div>
                <div className={styles.adminPageSectionRowItemRadioGroup}>
                  <label>
                    <input
                      type="radio"
                      name="projectUnitMode"
                      value="new"
                      checked={projectUnitMode === "new"}
                      onChange={() => {
                        setProjectUnitMode("new");
                        setSelectedTableName(null);
                        setProjectUnitMessage(null);
                      }}
                    />{" "}
                    Add New Table
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="projectUnitMode"
                      value="existing"
                      checked={projectUnitMode === "existing"}
                      onChange={() => {
                        setProjectUnitMode("existing");
                        setProjectUnitMessage(null);
                        handleFetchTableNames();
                      }}
                    />{" "}
                    Edit Existing Table
                  </label>
                </div>
                {projectUnitMode === "existing" && (
                  <div className={styles.adminPageSectionRowItemInput}>
                    <Select
                      className={styles.adminPageSectionRowItemInputSelect}
                      value={selectedTableName}
                      onChange={(option) => setSelectedTableName(option)}
                      options={existingTableNames}
                      isClearable={true}
                      isSearchable={true}
                      placeholder="Select Existing Table"
                      styles={AdminPageSelectStyles}
                      menuPortalTarget={menuPortalTarget}
                      menuPosition="fixed"
                      menuPlacement="auto"
                    />
                  </div>
                )}
                <div className={styles.adminPageSectionRowItemInput}>
                  <button onClick={handleDownloadTemplate}>
                    Download Template
                  </button>
                </div>
                <div className={styles.adminPageSectionRowItemInput}>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setProjectUnitFile(e.target.files[0])}
                  />
                  <button
                    onClick={handleUploadProjectUnits}
                    disabled={!projectUnitFile}
                  >
                    Upload CSV
                  </button>
                </div>
                {projectUnitMessage && (
                  <div className={styles.adminPageSectionRowItemContent}>
                    <p
                      style={{
                        color:
                          projectUnitMessage.type === "error" ? "red" : "green",
                      }}
                    >
                      {projectUnitMessage.text}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
          onSubmit={handleChangePassword}
        />
      )}
      {showUserStatusModal && (
        <ChangeUserStatusModal
          onClose={() => setShowUserStatusModal(false)}
          onSubmit={handleChangeUserStatus}
        />
      )}
    </div>
  );
};
